import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Dropdown, Pagination } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { filter } from 'lodash';
import moment from 'moment';
import React, { useState, useMemo } from 'react';
import { useAllDistrictTeacher, useCurrentUser } from 'store/users/selectors';
import styled from 'styled-components';
import usePopUpAtOnce from 'components/hooks/usePopUpAtOnce';
import { useAllBook } from 'store/books/selectors';
import { useVideos } from 'store/resources/selectors';
import { updateUI } from 'store/UI/actions';
import { useDispatch } from 'react-redux';
import Loading from 'components/Loading';
import SentryWrap from 'components/Sentry';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95vw', sm: '90vw', md: '80vw', lg: '70vw', xl: '60vw' },
  maxWidth: '1200px',
  minWidth: '320px',
  height: { xs: '80vh', sm: '75vh', md: '70vh', lg: '65vh', xl: '60vh' },
  maxHeight: '90vh',
  minHeight: '400px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0,
  borderRadius: '13px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

/* ----------------- styled components ----------------- */

const Wrapper = styled.div`
  border: 1px solid #d1d5db; /* gray-300 */
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  font-family: Arial, sans-serif;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background: linear-gradient(90deg, #004aad, #cb6ce6);
  color: #fff;
  padding: 12px 16px;
  font-weight: 600;
  flex-shrink: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;

  th,
  td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb; /* gray-200 */
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 35%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 25%;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 20%;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 20%;
  }

  thead tr {
    background-color: #f9fafb; /* gray-50 */
  }

  tbody tr:hover {
    background-color: #f3f4f6; /* gray-100 */
  }

  a {
    color: #29528a;
    font-weight: 500;
    text-decoration: none;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 8px 12px;
      font-size: 14px;
    }

    th:nth-child(1),
    td:nth-child(1) {
      width: 40%;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: 30%;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 15%;
    }

    th:nth-child(4),
    td:nth-child(4) {
      width: 15%;
    }
  }

  @media (max-width: 480px) {
    th,
    td {
      padding: 6px 8px;
      font-size: 12px;
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  border-bottom: 1px solid #e5e7eb;
  min-height: 0;
  
  /* Ensure table doesn't interfere with dropdown scrollbars */
  .ant-dropdown {
    z-index: 1050;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  min-height: 72px;
  align-items: center;
`;

const ScrollableDropdownWrapper = styled.div`
  .scrollable-dropdown .ant-dropdown-menu {
    max-height: 100px;
    overflow-y: auto;
    overflow-x: hidden;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;

      &:hover {
        background: #a8a8a8;
      }
    }

    /* Hide scrollbar for Firefox */
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
  }

  /* Ensure dropdown container doesn't have its own scrollbar */
  .scrollable-dropdown {
    overflow: visible !important;
  }

  /* Prevent any parent containers from adding scrollbars to dropdown */
  .ant-dropdown-menu-wrapper {
    overflow: visible !important;
  }

  /* Ensure only the menu itself has scrollbar */
  .ant-dropdown-menu {
    overflow-x: hidden !important;
  }
`;

const ShareByMe = ({ shareByMe, setShareByMe, data = [], redirectPage,from = '',loading=false }) => {
  const allDistrictTeacher = useAllDistrictTeacher();
  const [openDropdowns, setOpenDropdowns] = useState({});
  const currentUser = useCurrentUser();
  const { updatePopUpAtOnce } = usePopUpAtOnce();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const booksAllList = useAllBook();
  const video = useVideos();
  const dispatch = useDispatch();

  const findTeacherData = (id) => {
    const findShareBy = allDistrictTeacher.find((obj) => id === obj?.id);
    return findShareBy;
  };

  // Calculate paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  const handleDropdownOpenChange = (rowIndex, open) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [rowIndex]: open,
    }));
  };

  const handleDropdownClick = (event) => {
    event.stopPropagation();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleClose = () => {
    setShareByMe(false);
    updatePopUpAtOnce(false);
    setCurrentPage(1); // Reset to first page when closing
    dispatch(updateUI({ listViewSharedAndReceiveBooks: {} }));
  };
  const getBookName = (bookId) => {
    if(from === 'podcast'){
      const findVideo = video.find((obj) => bookId === obj?.id);
      return findVideo?.displayName || "N/A";
    }
    const findBook = booksAllList.find((obj) => bookId === obj?.id);

    return findBook?.description;
  };


  return (
    <SentryWrap>
      <ScrollableDropdownWrapper>
        <Modal
        open={shareByMe}
        onClose={() => handleClose()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div style={{ position: 'absolute', top: 10, right: 10 }}>
            <CloseIcon
              onClick={() => {
                handleClose();
              }}
              style={{ cursor: 'pointer', fontSize: 20, color: 'red' }}
            />
          </div>
          <Wrapper>
            <Header>Shared by me</Header>
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>{from?.trim() === 'podcast ' ? 'Book/Pg. #' : 'Book'}</th>
                    <th style={{ textAlign: 'center' }}>Shared to</th>
                    <th style={{ textAlign: 'center' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center' }}>
                        <Loading size={23} />
                      </td>
                    </tr>
                  ) : data?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          textAlign: 'center',
                          padding: '40px 16px',
                          color: '#6c757d',
                          fontSize: '16px',
                          fontWeight: '500',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <div style={{ fontSize: '32px' }}>📤</div>
                          <div>NO DATA</div>
                          <div
                            style={{
                              fontSize: '14px',
                              color: '#adb5bd',
                              fontWeight: '400',
                            }}
                          >
                            No shared content available
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedData?.map((row, i) => {
                      const filterAccessBy = filter(row?.access_by, (obj) => obj !== row?.userId);
                      const globalIndex = (currentPage - 1) * pageSize + i;
                      const isDropdownOpen = openDropdowns[globalIndex] || false;
                      const checkKeyonObj = Object.keys(row).includes('differentiation_data');
                      const checkSentenceStarterObj =
                        Object.keys(row).includes('sentence_starter_data');

                      return (
                        <tr key={globalIndex}>
                          <td>
                            <a onClick={() => redirectPage(row?.bookId, row?.pageId)} href="#">
                              {row?.name
                                ? row?.name
                                : checkSentenceStarterObj
                                  ? 'Sentence Starter # ' + `${globalIndex + 1}`
                                  : checkKeyonObj
                                    ? 'Differentiation Lesson # ' + `${globalIndex + 1}`
                                    : 'Super Lesson # ' + `${globalIndex + 1}`}
                            </a>
                          </td>
                          <td>
                          {from?.trim() === 'podcast'
                              ? getBookName(row?.bookId)
                              : `${getBookName(row?.bookId)}/Pg.${row?.pageId}`}
                          </td>
                          <td>
                            <Dropdown
                              menu={{
                                items: filterAccessBy?.map((obj) => ({
                                  label: `${findTeacherData(obj)?.firstName} ${findTeacherData(obj)?.lastName}`,
                                  key: obj?.id,
                                })),
                              }}
                              onOpenChange={(open) => {
                                if (filterAccessBy?.length === 1) {
                                  return;
                                }
                                handleDropdownOpenChange(globalIndex, open);
                              }}
                              open={isDropdownOpen}
                              trigger={['click']}
                              optionRender={(item) => {
                                return <div>{item.label}</div>;
                              }}
                              overlayClassName="scrollable-dropdown-shared-with-me-by-me"
                            >
                              <div
                                style={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                                onClick={handleDropdownClick}
                              >
                                {findTeacherData(filterAccessBy[0])?.firstName}
                                {findTeacherData(filterAccessBy[0])?.lastName}
                                {filterAccessBy?.length > 1 && (
                                  <DownOutlined
                                    className={isDropdownOpen ? 'rotate-icon' : ''}
                                    style={{
                                      transition: 'transform 0.3s',
                                      transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                  />
                                )}
                              </div>
                            </Dropdown>
                          </td>
                          <td>
                          {row?.shared_time ? moment(row.shared_time).format('MMMM Do, YYYY') : row?.created_at ? moment(row.created_at).format('MMMM Do, YYYY') : ''}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </StyledTable>
            </TableContainer>
            {data?.length > 0 && (
              <PaginationContainer>
                <Pagination
                  current={currentPage}
                  total={data.length}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                />
              </PaginationContainer>
            )}
          </Wrapper>
        </Box>
      </Modal>
    </ScrollableDropdownWrapper>
    </SentryWrap>
  );
};

export default ShareByMe;
